package com.bikompanion.data.analytics.web

import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

private const val ClientIdKey = "bikompanion.analytics.client_id"
private const val SessionIdKey = "bikompanion.analytics.session_id"
private const val LastEventKey = "bikompanion.analytics.last_event_at"
private val SessionTimeout = 30.minutes
private const val MinimumEngagementMillis = 1L
private val MaximumEngagementMillis = 30.seconds.inWholeMilliseconds

/**
 * Supplies the `client_id`, `session_id` and `engagement_time_msec` that the Analytics SDK would
 * otherwise derive itself. Without all three, GA4 stores a Measurement Protocol event and leaves
 * it out of every standard report.
 *
 * The client id is the visitor, so it lives in [visitorStorage] and outlives the tab. The session
 * is the visit, so it lives in [tabStorage] and expires after [SessionTimeout] of inactivity,
 * matching GA4's own session rule.
 */
internal class WebAnalyticsIdentity(
    private val visitorStorage: AnalyticsKeyValueStorage,
    private val tabStorage: AnalyticsKeyValueStorage,
    private val randomNumber: () -> Long,
) {
    fun resolve(nowMillis: Long): WebAnalyticsContext {
        val clientId = visitorStorage.read(ClientIdKey)?.takeIf(String::isNotBlank)
            ?: newClientId(nowMillis).also { visitorStorage.write(ClientIdKey, it) }

        val previousSessionId = tabStorage.read(SessionIdKey)?.takeIf(String::isNotBlank)
        val previousEventAt = tabStorage.read(LastEventKey)?.toLongOrNull()
        val isExpired = previousEventAt == null || nowMillis - previousEventAt >= SessionTimeout.inWholeMilliseconds
        val sessionId = if (previousSessionId == null || isExpired) (nowMillis / 1_000).toString() else previousSessionId

        val engagementTimeMillis = if (sessionId != previousSessionId || previousEventAt == null) {
            MinimumEngagementMillis
        } else {
            // The gap since the previous event stands in for reading time. A backgrounded tab
            // produces the same gap with nobody looking, so it is capped.
            (nowMillis - previousEventAt).coerceIn(MinimumEngagementMillis, MaximumEngagementMillis)
        }

        tabStorage.write(SessionIdKey, sessionId)
        tabStorage.write(LastEventKey, nowMillis.toString())
        return WebAnalyticsContext(clientId, sessionId, engagementTimeMillis)
    }

    /** The shape GA4's own tag uses: a random number and the first-seen second. */
    private fun newClientId(nowMillis: Long) = "${randomNumber().toString().trimStart('-')}.${nowMillis / 1_000}"
}
